'use client';

/**
 * ClientUiShell.tsx
 *
 * Client-wrapper for UiShell + rutebeskyttelse.
 *
 * Ansvarsområde:
 * 1. Skjule UI Shell på pre-auth-ruter (f.eks. /login)
 * 2. Rutebeskyttelse — redirect til /login hvis ikke autentisert
 * 3. Redirect til /dashboard hvis autentisert og på /login
 * 4. Vise loading-state mens auth-state initialiseres (unngår flimring)
 * 5. Lenis-aktig myk scrolling på alle sider uten innlogging (fronten av inSuite): PublicSmoothScroll, kun på ruter uten UI Shell
 *
 * UiShell bruker usePathname() (client hook), så den må rendres på klientsiden.
 * UiShell er ren React (ingen web components), så next/dynamic + ssr: false
 * er ikke nødvendig.
 */

import { ReactNode, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import UiShell from '@/sections/ui-shell/UiShell';
import PublicSmoothScroll from '@/components/PublicSmoothScroll';
import LoginTransition from '@/sections/login/LoginTransition';
import { LOGIN_ANIM_MS } from '@/sections/login/login-timing';

// Ruter som skal rendres UTEN UI Shell (pre-auth, standalone-sider)
// NB: /bad-gateway er også her — 502-siden skal kunne vises uansett
// auth-tilstand (backend kan være nede, JWT kan være utløpt).
// NB: /session-expired er også her — 401-siden vises NETTOPP når JWT er
// utløpt, så den kan ikke kreve autentisering (ville gitt redirect-loop).
// NB: / er også her — rotdomenet er offentlig og skal IKKE redirecte
// til /login (brukerinstruks 2026-09-22).
const ROUTES_WITHOUT_SHELL = ['/', '/webhotell', '/infiles', '/vps', '/wordpress', '/nodejs', '/gpu', '/domener', '/forside-2', '/forside-3', '/forside-4', '/forside-5', '/login', '/bad-gateway', '/session-expired'];

// Ruter som krever autentisering (alt unntatt /, /login og /bad-gateway)
function isProtectedRoute(pathname: string): boolean {
  return !ROUTES_WITHOUT_SHELL.some(
    (route) => pathname === route || pathname.startsWith(route + '/')
  );
}

export default function ClientUiShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading, holdRedirect, setHoldRedirect, loginTransition, setLoginTransition } = useAuth();

  // Innloggingsanimasjonen ligger her (ikke i LoginSection) så den overlever
  // navigasjonen til /dashboard. «leaving» venter til siden har byttet fra /login
  // og er tegnet (to animasjonsrammer), slik at dashbordet er på plass under.
  const [painted, setPainted] = useState(false);
  const onLogin = pathname === '/login';
  useEffect(() => {
    if (onLogin || !loginTransition) return;
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setPainted(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [onLogin, loginTransition]);

  // Sjekk om gjeldende rute skal være uten UI Shell
  const hideShell = ROUTES_WITHOUT_SHELL.some(
    (route) => pathname === route || pathname.startsWith(route + '/')
  );

  // Rutebeskyttelse — kjør etter at auth-state er ferdig initialisert
  useEffect(() => {
    if (loading) return;

    // Ikke autentisert + beskyttet rute → redirect til /login
    if (!user && isProtectedRoute(pathname)) {
      router.replace('/login');
      return;
    }

    // Autentisert + på /login → redirect til /dashboard
    // (unntatt mens innloggingsanimasjonen spilles — LoginSection navigerer selv)
    if (user && pathname === '/login' && !holdRedirect) {
      router.replace('/dashboard');
    }
  }, [user, loading, holdRedirect, pathname, router]);

  // Innloggingsanimasjonen rendres over alt (også over login-siden og dashbordet)
  const overlay = loginTransition && (
    <LoginTransition
      phase={loginTransition.phase === 'leaving' && !(painted && !onLogin) ? 'done' : loginTransition.phase}
      durationMs={LOGIN_ANIM_MS}
      name={loginTransition.name}
      onLeft={() => {
        setLoginTransition(null);
        setHoldRedirect(false);
        setPainted(false);
      }}
    />
  );

  let content: ReactNode;
  if (loading || (!user && isProtectedRoute(pathname))) {
    // Vis ingenting mens auth-state initialiseres / redirect pågår (unngår flimring)
    content = null;
  } else if (hideShell) {
    // Skjul UI Shell på pre-auth-ruter
    content = children;
  } else {
    content = <UiShell>{children}</UiShell>;
  }

  return (
    <>
      {content}
      {overlay}
    </>
  );
}
